import { Avatar, Box, Button, Grid, LinearProgress, TextField, Typography } from '@mui/material';
import grey from '@mui/material/colors/grey';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { Prompt, useHistory } from 'react-router';

import { CREATE, DataAPIContext } from '../api';
import AccessTagsDropdown from '../components/AccessTagsDropdown';
import FormActionButtons from '../components/FormActionButtons';
import FormLicenseeContext from '../components/FormLicenseeContext';
import LicenseeFormField from '../components/LicenseeFormField';
import PageMeta from '../components/PageMeta';
import { ContextStore } from '../store/contextStore';
import { disableMuiButton, enableMuiButton } from '../utils';

const useStyles = makeStyles((theme) => ({
    root: {
        border: `1px solid ${grey[200]}`,
        borderRadius: '5px',
        width: '90%',
        [theme.breakpoints.down('sm')]: {
            width: '99.5%',
        },
    },
    gridRoot: {
        padding: '1rem',
    },
    buttonPanel: {
        marginTop: theme.spacing(4),
        backgroundColor: grey[100],
    },
}));

const ActivityCategoryCreate = (props) => {
    const dataAPI = React.useContext(DataAPIContext);
    const { isFranchise, currentLicensee } = React.useContext(ContextStore);
    const resource = 'activity_category';
    const history = useHistory();
    const classes = useStyles();
    const saveRef = React.useRef(null);
    const [syncActive, setSyncActive] = React.useState(false);
    const { enqueueSnackbar } = useSnackbar();
    const [category, setCategory] = React.useState({ title: '', atag: [], tour_operator: currentLicensee || null, _changed: false });
    const [iconFile, setIconFile] = React.useState(null);
    const [iconPreview, setIconPreview] = React.useState(null);
    const [leavePromptAcknowledged, setLeavePromptAcknowledged] = React.useState(false);

    const handleChange = (ev) => {
        setCategory((prev) => ({ ...prev, [ev.target.name]: ev.target.value, _changed: true }));
    };

    const onAccessTagsChange = (ev, atags) => {
        setCategory((prev) => ({ ...prev, atag: atags, _changed: true }));
    };

    const onLicenseeChange = (id) => {
        setCategory((prev) => ({ ...prev, tour_operator: id, _changed: true }));
    };

    const handleIconChange = (ev) => {
        const file = ev.target.files?.[0];
        if (file) {
            setIconFile(file);
            setIconPreview(URL.createObjectURL(file));
            setCategory((prev) => ({ ...prev, _changed: true }));
        }
    };

    const handleSave = (ev) => {
        if (!category.title) return;
        setSyncActive(true);
        disableMuiButton(saveRef.current);
        (async () => {
            try {
                const payload = { title: category.title, atag: category.atag, ...(category.tour_operator ? { tour_operator: category.tour_operator } : {}) };
                if (iconFile) {
                    payload.image = iconFile;
                }
                const res = await dataAPI(CREATE, resource, { data: payload, upload: true });
                if (res.statusCode === 201) {
                    enqueueSnackbar('Activity category created', { variant: 'success' });
                    setCategory((prev) => ({ ...prev, _changed: false }));
                    history.push('/activity-categories');
                } else {
                    enqueueSnackbar(res.data?.message || 'Error creating category', { variant: 'error' });
                }
            } finally {
                setSyncActive(false);
                if (saveRef?.current) {
                    enableMuiButton(saveRef.current);
                }
            }
        })();
    };

    const canSave = !!category.title && (!isFranchise || !!category.tour_operator);
    const licenseeError = isFranchise && !category.tour_operator ? { error: true, helperText: 'Licensee is required' } : null;

    return (
        <div className={classes.root}>
            <PageMeta title="Create New Activity Category" />
            <Prompt
                when={!!category._changed && leavePromptAcknowledged === false}
                message={() => {
                    setLeavePromptAcknowledged(true);
                    return 'Are you sure you want to leave without saving changes?';
                }}
            />
            <FormLicenseeContext.Provider value={category.tour_operator}>
            <div className={classes.gridRoot}>
                <Grid container direction="row" spacing={2}>
                    <Grid item xs={12}>
                        <LinearProgress color="primary" style={{ visibility: syncActive ? 'visible' : 'hidden' }} />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="h6">Create New Activity Category</Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <LicenseeFormField value={category.tour_operator} onChange={onLicenseeChange} error={licenseeError?.error} helperText={licenseeError?.helperText} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField required variant="filled" size="small" fullWidth label="Category Title" name="title" onChange={handleChange} value={category.title} />
                    </Grid>
                    <Grid item xs={12}>
                        <Box display="flex" alignItems="center" gap={2}>
                            <Button variant="outlined" component="label" size="small">
                                {iconFile ? 'Change Icon' : 'Upload Icon'}
                                <input type="file" hidden accept="image/*" onChange={handleIconChange} />
                            </Button>
                            {iconPreview && <Avatar src={iconPreview} variant="rounded" sx={{ width: 40, height: 40 }} />}
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <AccessTagsDropdown value={category.atag} onChange={onAccessTagsChange} />
                    </Grid>
                </Grid>
                <Box className={classes.buttonPanel} sx={{ py: 1, px: 0 }}>
                    <FormActionButtons
                        submitRef={saveRef}
                        onSubmit={handleSave}
                        submitDisabled={!canSave}
                        onCancel={() => history.push('/activity-categories')}
                    />
                </Box>
            </div>
            </FormLicenseeContext.Provider>
        </div>
    );
};

export default ActivityCategoryCreate;
